import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Btn } from "./ui";
import { useSettings } from "../../context/SettingsContext";
import { useContact } from "../../context/ContactContext";

const links = [
  { label: "Work", href: "#work" },
  { label: "Why Us", href: "#why" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = () => {
  const { settings } = useSettings();
  const { openTally } = useContact();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const brand = settings?.brand_name || "Bishal Banerjee";

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
      style={{
        background: scrolled ? "rgba(5,5,5,0.7)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid var(--border-color)" : "1px solid transparent",
      }}
      data-testid="site-nav"
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-brand" className="flex items-center gap-3">
          <img
            src="/bishal-portrait.jpg"
            alt="Bishal Banerjee"
            className="object-cover"
            style={{ width: "36px", height: "44px", borderRadius: "8px", objectFit: "cover", objectPosition: "center" }}
            data-testid="nav-brand-portrait"
          />
          <span className="font-heading text-base md:text-lg font-semibold tracking-tight">{brand}</span>
        </a>

        <div className="hidden md:flex items-center gap-9 text-sm" style={{ color: "var(--text-secondary)" }}>
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="relative transition-colors duration-300 hover:text-[var(--text-primary)]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Btn as="button" onClick={openTally} variant="solid" className="hidden sm:inline-flex !py-2.5 !px-5" data-testid="nav-start-project">
            Start a Project
          </Btn>
          <button
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t"
            style={{ background: "rgba(5,5,5,0.95)", borderColor: "var(--border-color)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <Btn as="button" onClick={() => { setOpen(false); openTally(); }} variant="solid" className="mt-2 w-full" data-testid="nav-mobile-start-project">
                Start a Project
              </Btn>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
